import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "./api/axiosInstance";
import "./CrudTable.css";

// --- Helper Functions ---

// Determine input type based on column type.
const getInputType = (type) => {
  switch (type) {
    case "int":
    case "float":
    case "money":
      return "number";
    case "date":
      return "date";
    default:
      return "text";
  }
};

const getStep = (type) => {
  if (type === "money" || type === "float") return "0.01";
  if (type === "int") return "1";
  return undefined;
};

// Render a cell value safely.
const renderCell = (value, col) => {
  if (col.type === "bool") {
    return value ? <span className="check-icon">✔</span> : "";
  }
  if (col.type === "bigtext")
    return value ? JSON.stringify(value) : "";
  if (col.type === "jsontext")
    return <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, fontSize: '0.95em' }}>
  {JSON.stringify(value, null, 2)}
</pre>;
  if (col.type === "ref") {
    const field = col.renderField || "name";
    if (Array.isArray(value)) {
      return value.map((item) => item[field]).join(", ");
    }
    return value && typeof value === "object" ? value[field] || "" : "";
  }
  if (col.type === "money") {
    return value !== undefined && value !== null && !isNaN(value) ? (
      <span className="money-cell">{Number(value).toFixed(2)}</span>
    ) : (
      ""
    );
  }
  if (typeof value === "object" && value !== null) {
    return JSON.stringify(value);
  }
  return value;
};

// --- Ref Field Components ---

/**
 * RefTypeahead
 * For ref fields with default control (multi-search).
 * Allows multiple selections via a typeahead interface.
 */
function RefTypeahead({ column, value, onChange, baseUrl }) {
  const selectedValues = Array.isArray(value) ? value : [];
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const renderField = column.renderField || "name";

  const fetchSuggestions = async (search) => {
    try {
      let url = `${baseUrl}/${column.entity}?search=${encodeURIComponent(
        search
      )}&limit=10`;
      if (column.searchFields) {
        url += `&searchFields=${encodeURIComponent(column.searchFields)}`;
      }
      const response = await axiosInstance.get(url);
      setSuggestions(response.data.items);
    } catch (error) {
      console.error("Error fetching suggestions", error);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (val.length > 1) {
      fetchSuggestions(val);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    const newSuggestion = { ...suggestion, r_id: suggestion._id };
    delete newSuggestion._id;
    if (!selectedValues.some((item) => item.r_id === newSuggestion.r_id)) {
      onChange([...selectedValues, newSuggestion]);
    }
    setQuery("");
    setShowDropdown(false);
    setSuggestions([]);
  };

  const handleRemoveItem = (item) => {
    const newValues = selectedValues.filter((v) => v.r_id !== item.r_id);
    onChange(newValues);
  };

  return (
    <div className="ref-typeahead">
      <div className="selected-chips">
        {selectedValues.map((item) => (
          <span key={item.r_id} className="chip">
            {item[renderField]}
            <button type="button" onClick={() => handleRemoveItem(item)}>
              x
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        placeholder={`Search ${column.entity}`}
        value={query}
        onChange={handleInputChange}
        onFocus={() => setShowDropdown(suggestions.length > 0)}
      />
      {showDropdown && suggestions.length > 0 && (
        <ul className="suggestions-dropdown">
          {suggestions.map((suggestion) => (
            <li
              key={suggestion._id}
              onClick={() => handleSelectSuggestion(suggestion)}
            >
              {suggestion[renderField]}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * RefCheckbox
 * For ref fields with control "checkbox".
 * Displays all rows of the referenced entity as checkboxes.
 * Supports multiple selections.
 */
function RefCheckbox({ column, value, onChange, baseUrl }) {
  const selectedValues = Array.isArray(value) ? value : [];
  const [options, setOptions] = useState([]);
  const renderField = column.renderField || "name";

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        let url = `${baseUrl}/${column.entity}?limit=1000`;
        if (column.searchFields) {
          url += `&searchFields=${encodeURIComponent(column.searchFields)}`;
        }
        const response = await axiosInstance.get(url);
        setOptions(response.data.items);
      } catch (error) {
        console.error("Error fetching options for checkbox ref", error);
      }
    };
    fetchOptions();
  }, [baseUrl, column.entity, column.searchFields]);

  const handleCheckboxChange = (option, checked) => {
    const newOption = { ...option, r_id: option._id };
    delete newOption._id;
    let newValues = [];
    if (checked) {
      newValues = [...selectedValues];
      if (!newValues.some((item) => item.r_id === newOption.r_id)) {
        newValues.push(newOption);
      }
    } else {
      newValues = selectedValues.filter((item) => item.r_id !== newOption.r_id);
    }
    onChange(newValues);
  };

  return (
    <div className="ref-checkbox">
      {options.map((option) => {
        const isChecked = selectedValues.some(
          (item) => item.r_id === option._id
        );
        return (
          <div key={option._id} className="checkbox-option">
            <label>
              <input
                type="checkbox"
                checked={isChecked || false}
                onChange={(e) => handleCheckboxChange(option, e.target.checked)}
              />
              <span className="checkbox-label-text">{option[renderField]}</span>
            </label>
          </div>
        );
      })}
    </div>
  );
}

/**
 * RefRadio
 * For ref fields with control "radiobutton".
 * Displays all rows of the referenced entity as radio buttons.
 * Supports single selection.
 */
function RefRadio({ column, value, onChange, baseUrl }) {
  const [options, setOptions] = useState([]);
  const renderField = column.renderField || "name";

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        let url = `${baseUrl}/${column.entity}?limit=1000`;
        if (column.searchFields) {
          url += `&searchFields=${encodeURIComponent(column.searchFields)}`;
        }
        const response = await axiosInstance.get(url);
        setOptions(response.data.items);
      } catch (error) {
        console.error("Error fetching options for radio ref", error);
      }
    };
    fetchOptions();
  }, [baseUrl, column.entity, column.searchFields]);

  const handleRadioChange = (option) => {
    const newOption = { ...option, r_id: option._id };
    delete newOption._id;
    onChange(newOption);
  };

  return (
    <div className="ref-radio">
      {options.map((option) => {
        const isSelected = value && value.r_id === option._id;
        return (
          <div key={option._id} className="radio-option">
            <label>
              <input
                type="radio"
                name={column.field}
                checked={isSelected || false}
                onChange={() => handleRadioChange(option)}
              />
              <span className="radio-label-text">{option[renderField]}</span>
            </label>
          </div>
        );
      })}
    </div>
  );
}

/**
 * RefSingle
 * For ref fields with control "single".
 * Similar to RefTypeahead but allows only a single selection.
 * Displays the selected value's name in the input field.
 */
function RefSingle({ column, value, onChange, baseUrl }) {
  const renderField = column.renderField
    || (column.searchFields
        ? column.searchFields.split(",")[0].trim()
        : "name");
  const [query, setQuery] = useState(
    value && value[renderField] ? value[renderField] : ""
  );
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const fetchSuggestions = async (search) => {
    try {
      let url = `${baseUrl}/${column.entity}?search=${encodeURIComponent(
        search
      )}&limit=10`;
      if (column.searchFields) {
        url += `&searchFields=${encodeURIComponent(column.searchFields)}`;
      }
      const response = await axiosInstance.get(url);
      setSuggestions(response.data.items);
      setHighlightedIndex(-1);
    } catch (error) {
      console.error("Error fetching suggestions for single ref", error);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (val.length > 0) {
      fetchSuggestions(val);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    const newSuggestion = { ...suggestion, r_id: suggestion._id };
    delete newSuggestion._id;
    onChange(newSuggestion);
    setQuery(newSuggestion[renderField]);
    setShowDropdown(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
  };

  const handleClear = () => {
    onChange(null);
    setQuery("");
    setShowDropdown(false);
    setSuggestions([]);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) =>
        prevIndex + 1 >= suggestions.length ? 0 : prevIndex + 1
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) =>
        prevIndex - 1 < 0 ? suggestions.length - 1 : prevIndex - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        handleSelectSuggestion(suggestions[highlightedIndex]);
      }
    }
  };

  return (
    <div className="ref-single">
      <input
        type="text"
        placeholder={`Search ${column.entity}`}
        value={query}
        onChange={handleInputChange}
        onFocus={() => setShowDropdown(suggestions.length > 0)}
        onKeyDown={handleKeyDown}
      />
      {showDropdown && suggestions.length > 0 && (
        <ul className="suggestions-dropdown">
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion._id}
              onClick={() => handleSelectSuggestion(suggestion)}
              className={index === highlightedIndex ? "highlighted" : ""}
            >
              {suggestion[renderField]}
            </li>
          ))}
        </ul>
      )}
      {value && (
        <button type="button" onClick={handleClear}>
          Clear
        </button>
      )}
    </div>
  );
}

// --- Main CrudTable Component ---

/**
 * CrudTable Component
 * A reusable CRUD table with search, pagination, sorting, modal add/edit,
 * and support for various field types including reference fields.
 *
 * Optional props:
 * - selectable (boolean): If true, rows are clickable.
 * - onRowSelect (function): Callback when a row is selected.
 * - selectedRowId (string): The _id of the selected row for highlighting.
 * - preSave (function): Function to transform modalData before saving.
 * - filter (object): Extra filter key/value pairs appended as query parameters.
 *
 * The "endpoint" prop is used to compute baseUrl and is passed down to helper components.
 */
function CrudTable({
  endpoint,
  columns,
  selectable = false,
  onRowSelect,
  selectedRowId,
  preSave,
  filter: externalFilter,
}) {
  const visibleColumns = columns.filter(col => !col.hidden);

  // Compute baseUrl from endpoint (remove the last segment).
  const baseUrl = endpoint.substring(0, endpoint.lastIndexOf("/"));

  // State for table data and search.
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [distinctOptions, setDistinctOptions] = useState({});

  // Pagination state.
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  // Sorting state.
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // Modal state for add/edit.
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [modalData, setModalData] = useState({});
  const [jsonError, setJsonError] = useState("");

  const [loading, setLoading] = useState(true);

  // Fetch distinct values for columns with filter:true
  useEffect(() => {
    columns.filter(col => col.filter).forEach(col => {
      const key = col.filterField || col.field;
      axiosInstance.get(`${endpoint}/distinct/${key}`)
        .then(res => setDistinctOptions(prev => ({ ...prev, [key]: res.data.values || [] })))
        .catch(err => console.error(`distinct error ${key}:`, err));
    });
  }, [endpoint, columns]);

  // Build query parameters string including extra filters.
  const buildQueryParams = useCallback(() => {
    let params = `?page=${page}&limit=${limit}`;
    if (searchQuery) params += `&search=${encodeURIComponent(searchQuery)}`;
    if (sortField) params += `&sortField=${sortField}&sortOrder=${sortOrder}`;
    // Build searchFields parameter from columns with search:true.
    const searchableFields = columns
      .filter((col) => col.search)
      .map((col) => col.entity? col.searchFields? 
        col.searchFields.split(',').map((sf)=>`${col.entity}.${sf}`).join(',')
      :`${col.entity}.name`: col.field)
      .join(",");
    if (searchQuery && searchableFields) {
      params += `&searchFields=${encodeURIComponent(searchableFields)}`;
    }
    Object.entries(filterValues).forEach(([field, val]) => {
      if (val !== "") params += `&${field}=${encodeURIComponent(val)}`;
    });
    if (externalFilter) {
      Object.entries(externalFilter).forEach(([k, v]) => {
        params += `&${k}=${encodeURIComponent(v)}`;
      });
    }
    return params;
  }, [page, limit, searchQuery, sortField, sortOrder, filterValues, externalFilter, columns]);

  // Memoize fetchData so it's stable unless its inputs change
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const queryParam = buildQueryParams();
      const response = await axiosInstance.get(`${endpoint}${queryParam}`);
      setItems(response.data.items);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Error fetching data: ", error);
    } finally {
      setLoading(false);
    }
  }, [endpoint, buildQueryParams]);

  // Now the effect only needs to depend on the stable fetchData
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openModal = (mode, item = {}) => {
    setModalMode(mode);
    setModalData(mode === "edit" ? { ...item } : {});
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalData({});
  };

  const handleModalInputChange = (e) => {
    const { name, value, type } = e.target;
    const parsedValue =
      type === "number" ? (value === "" ? "" : parseFloat(value)) : value;
    setModalData((prev) => ({ ...prev, [name]: parsedValue }));
  };

  const handleSaveModal = async () => {
    // Validate JSON fields
    for (let col of columns) {
      if (col.type === "jsontext") {
        const raw = modalData[col.field] || "";
        if (typeof raw === 'string') // only if it is string state
        try {
          modalData[col.field] = raw ? JSON.parse(raw) : {};
        } catch (err) {
          setJsonError(`Invalid JSON in '${col.headerName}': ${err.message}`);
          return;
        }
      }
    }
    setJsonError("");
    try {
      const dataToSave = preSave ? preSave(modalData) : modalData;
      if (modalMode === "edit") {
        await axiosInstance.put(`${endpoint}/${modalData._id}`, dataToSave);
      } else {
        await axiosInstance.post(endpoint, dataToSave);
      }
      closeModal();
      fetchData();
    } catch (error) {
      console.error("Error saving data: ", error);
    }
  };

  const handleDeleteItem = async (id, e) => {
    if (e) e.stopPropagation();
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this item?"
    );
    if (!confirmDelete) return;
    try {
      await axiosInstance.delete(`${endpoint}/${id}`);
      fetchData();
    } catch (error) {
      console.error("Error deleting data: ", error);
    }
  };

  const goToPreviousPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const goToNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const handleSearch = (e) => { setSearchQuery(e.target.value); setPage(1); };
  const handleFilterChange = (field, val) => { setFilterValues((p) => ({ ...p, [field]: val })); setPage(1); };
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const renderSortIndicator = (field) => {
    if (sortField === field) {
      return sortOrder === "asc" ? " ▲" : " ▼";
    }
    return "";
  };

  return (
    <div>
      <div className="search-filter-container">
        <input type="text" placeholder="Search..." value={searchQuery} onChange={handleSearch} />
        {columns.filter(c=>c.filter).map(col=>{
          const key=col.filterField||col.field;
          return (
            <select key={key} value={filterValues[key]||""}
              onChange={e=>handleFilterChange(key,e.target.value)}>
              <option value="">All {col.headerName}</option>
              {(distinctOptions[key]||[]).map(val=>(<option key={val} value={val}>{val}</option>))}
            </select>
          );
        })}
      </div>
      <div className="crubtable-wrapper">
        <div className="crudtable-container">
        { loading && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}
        <table border="1" cellPadding="8" cellSpacing="0">
          <thead>
            <tr>
              <th className="action-col"></th>
              {visibleColumns.map((col) => (
                <th
                  key={col.field}
                  onClick={() => handleSort(col.field)}
                  style={{ cursor: "pointer" }}
                >
                  {col.headerName}
                  {renderSortIndicator(col.field)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item._id}
                onClick={
                  selectable ? () => onRowSelect && onRowSelect(item) : undefined
                }
                className={
                  selectable && selectedRowId === item._id ? "selected-row" : ""
                }
              >
                <td className="action-col">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal("edit", item);
                    }}
                    title="Edit"
                    className="icon-btn"
                  >
                    <span role="img" aria-label="edit">
                      ✏️
                    </span>
                  </button>
                  <button
                    onClick={(e) => handleDeleteItem(item._id, e)}
                    title="Delete"
                    className="icon-btn"
                  >
                    <span role="img" aria-label="delete">
                      🗑️
                    </span>
                  </button>
                </td>
                {visibleColumns.map((col) => (
                  <td key={col.field}>{renderCell(item[col.field], col)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      <div className="pagination">
        <button onClick={goToPreviousPage} disabled={page <= 1}>
          Previous
        </button>
        <span style={{ margin: "0 1rem" }}>
          Page {page} of {totalPages}
        </span>
        <button onClick={goToNextPage} disabled={page >= totalPages}>
          Next
        </button>
        <span style={{ marginLeft: "1rem" }}>
          Items per page:{" "}
          <select
            value={limit}
            onChange={(e) => {
              setLimit(parseInt(e.target.value));
              setPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </span>
      </div>
      <br />
      <button className="primary-button" onClick={() => openModal("add")}>Add New</button>

      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{modalMode === "edit" ? "Edit Item" : "Add New Item"}</h3>
            <form className="modal-form">
              {columns.map((col) => (
                <div key={col.field} className={`modal-field ${col.type === "bool" ? "bool-field" : ""}`}>
                  <label>{col.headerName}:</label>
                  {col.type === "ref" ? (
                    col.readOnly ? (
                      <span>{renderCell(modalData[col.field], col)}</span>
                    ) : col.control === "checkbox" ? (
                      <RefCheckbox
                        column={col}
                        value={modalData[col.field] || []}
                        onChange={(newVal) =>
                          setModalData((prev) => ({
                            ...prev,
                            [col.field]: newVal,
                          }))
                        }
                        baseUrl={baseUrl}
                      />
                    ) : col.control === "radiobutton" ? (
                      <RefRadio
                        column={col}
                        value={modalData[col.field] || null}
                        onChange={(newVal) =>
                          setModalData((prev) => ({
                            ...prev,
                            [col.field]: newVal,
                          }))
                        }
                        baseUrl={baseUrl}
                      />
                    ) : col.control === "single" ? (
                      <RefSingle
                        column={col}
                        value={modalData[col.field] || null}
                        onChange={(newVal) =>
                          setModalData((prev) => ({
                            ...prev,
                            [col.field]: newVal,
                          }))
                        }
                        baseUrl={baseUrl}
                      />
                    ) : (
                      <RefTypeahead
                        column={col}
                        value={modalData[col.field] || []}
                        onChange={(newVal) =>
                          setModalData((prev) => ({
                            ...prev,
                            [col.field]: newVal,
                          }))
                        }
                        baseUrl={baseUrl}
                      />
                    )
                  ) : col.type === "bool" ? (
                    col.readOnly ? (
                      <span>{renderCell(modalData[col.field], col)}</span>
                    ) : (
                      <input
                        type="checkbox"
                        name={col.field}
                        checked={!!modalData[col.field]}
                        onChange={(e) =>
                          setModalData((prev) => ({
                            ...prev,
                            [col.field]: e.target.checked,
                          }))
                        }
                      />
                    )
                  ) : (col.type === "bigtext") ? (
                    col.readOnly ? (
                      <textarea
                        name={col.field}
                        value={modalData[col.field] || ""}
                        readOnly
                        rows="5"
                      />
                    ) : (
                      <textarea
                        name={col.field}
                        value={modalData[col.field] || ""}
                        onChange={handleModalInputChange}
                        rows="5"
                      />
                    )
                  ) : (col.type === "jsontext") ? (
                    col.readOnly ? (
                      <textarea
                        name={col.field}
                        value={typeof modalData[col.field] === 'string'
                          ? modalData[col.field]
                          : modalData[col.field]
                            ? JSON.stringify(modalData[col.field], null, 2)
                            : ''}
                        readOnly
                        rows="10"
                      />
                    ) : (
                      <textarea
                        name={col.field}
                        value={typeof modalData[col.field] === 'string'
                          ? modalData[col.field]
                          : modalData[col.field]
                            ? JSON.stringify(modalData[col.field], null, 2)
                            : ''}
                        onChange={handleModalInputChange}
                        rows="10"
                      />
                    )
                  ) : col.readOnly ? (
                    <input
                      type={getInputType(col.type)}
                      name={col.field}
                      value={modalData[col.field] || ""}
                      readOnly
                    />
                  ) : (
                    <input
                      type={getInputType(col.type)}
                      name={col.field}
                      value={modalData[col.field] || ""}
                      onChange={handleModalInputChange}
                      step={getStep(col.type)}
                    />
                  )}
                </div>
              ))}
            </form>
            <div style={{color: 'red'}}>{ jsonError }</div>
            <div className="modal-buttons">
              <button className="primary-button" onClick={handleSaveModal}>Save</button>
              <button className="primary-button" onClick={closeModal} style={{ marginLeft: "1rem" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CrudTable;
